import { createGlobalStyle } from 'styled-components';

/**
 * Fussaki Design System — Dark Ocean Premium Theme
 *
 * Inspired by:
 *   - diveyoung.com: energetic accents, card layouts, scroll animations
 *   - kineticspearfishing.com: dark immersive base, bold typography, trophy feel
 *
 * Palette:
 *   Deep ocean dark base → teal/electric blue accents → gold CTAs → white text
 */

const GlobalStyles = createGlobalStyle`
  :root {
    /* --- Primary Palette --- */
    --bg-primary: #0A1628;        /* Deep ocean dark — main background */
    --bg-secondary: #0F1F35;      /* Slightly lighter panel backgrounds */
    --bg-card: #132742;           /* Card/section backgrounds */
    --bg-elevated: #1A3050;       /* Hover states, elevated surfaces */

    /* --- Accent Colors --- */
    --accent-teal: #00C9B1;       /* Primary accent — links, highlights, active states */
    --accent-gold: #F4AA00;       /* CTA buttons, urgency, premium feel */
    --accent-blue: #2196F3;       /* Info, secondary links */
    --accent-coral: #FF6B6B;      /* Error, danger, low availability */

    /* --- Text Colors --- */
    --text-primary: #FFFFFF;       /* Headings, high-priority text */
    --text-secondary: #B0C4D8;    /* Body text, descriptions */
    --text-muted: #6B8299;        /* Captions, helper text, disabled */
    --text-inverse: #0A1628;      /* Text on light/gold backgrounds */

    /* --- Borders & Dividers --- */
    --border-color: rgba(255, 255, 255, 0.08);
    --border-hover: rgba(0, 201, 177, 0.3);

    /* --- Gradients --- */
    --gradient-hero: linear-gradient(135deg, rgba(10, 22, 40, 0.85) 0%, rgba(15, 31, 53, 0.7) 100%);
    --gradient-card: linear-gradient(180deg, #132742 0%, #0F1F35 100%);
    --gradient-accent: linear-gradient(135deg, #00C9B1 0%, #2196F3 100%);
    --gradient-gold: linear-gradient(135deg, #F4AA00 0%, #FF8C00 100%);

    /* --- Shadows --- */
    --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.3);
    --shadow-md: 0 4px 20px rgba(0, 0, 0, 0.4);
    --shadow-lg: 0 8px 40px rgba(0, 0, 0, 0.5);
    --shadow-glow-teal: 0 0 20px rgba(0, 201, 177, 0.15);
    --shadow-glow-gold: 0 0 20px rgba(244, 170, 0, 0.15);

    /* --- Typography --- */
    --font-heading: 'Montserrat', 'Helvetica Neue', sans-serif;
    --font-body: 'Inter', 'Roboto', sans-serif;

    /* --- Spacing --- */
    --section-padding: 5rem 0;
    --section-padding-mobile: 3rem 0;
    --container-max: 1200px;
    --border-radius: 8px;
    --border-radius-lg: 16px;

    /* --- Transitions --- */
    --transition-fast: 0.2s ease;
    --transition-base: 0.3s ease;
    --transition-slow: 0.5s ease;

    /* --- Legacy compat (some styled-components still reference these) --- */
    --primary-color: var(--accent-teal);
    --secondary-color: var(--bg-primary);
    --accent-color: var(--accent-gold);
    --light-color: var(--text-primary);
    --dark-color: var(--bg-primary);
    --grey-color: var(--bg-secondary);
    --text-color: var(--text-secondary);
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-body);
    line-height: 1.6;
    color: var(--text-secondary);
    background-color: var(--bg-primary);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    text-decoration: none;
    color: var(--accent-teal);
    transition: color var(--transition-fast);

    &:hover {
      color: var(--accent-gold);
    }
  }

  button {
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: var(--text-primary);
  }

  h1 { font-size: clamp(2.5rem, 5vw, 4rem); }
  h2 { font-size: clamp(2rem, 4vw, 3rem); }
  h3 { font-size: clamp(1.5rem, 3vw, 2rem); }

  p {
    margin-bottom: 1rem;
  }

  ::selection {
    background-color: var(--accent-teal);
    color: var(--bg-primary);
  }

  /* --- Scrollbar --- */
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: var(--bg-primary);
  }
  ::-webkit-scrollbar-thumb {
    background: var(--bg-elevated);
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--accent-teal);
  }

  .container {
    width: 100%;
    max-width: var(--container-max);
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  .section {
    padding: var(--section-padding);
  }

  /* ─── React DatePicker global dark overrides ─── */
  .react-datepicker-popper {
    z-index: 100 !important;
  }

  .react-datepicker {
    background-color: var(--bg-card) !important;
    border: 1px solid var(--border-color) !important;
    color: var(--text-secondary) !important;
    font-family: var(--font-body) !important;
  }

  .react-datepicker__header {
    background-color: var(--bg-elevated) !important;
    border-bottom: 1px solid var(--border-color) !important;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    color: var(--text-primary) !important;
  }

  .react-datepicker__day-name {
    color: var(--accent-teal) !important;
  }

  .react-datepicker__day {
    color: var(--text-secondary) !important;
  }

  .react-datepicker__day:hover {
    background-color: var(--bg-elevated) !important;
    color: var(--text-primary) !important;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: var(--accent-gold) !important;
    color: var(--text-inverse) !important;
  }

  .react-datepicker__day--today {
    border: 1px solid var(--accent-teal) !important;
    border-radius: 50% !important;
  }

  .react-datepicker__day--disabled {
    color: var(--text-muted) !important;
    opacity: 0.3 !important;
  }

  .react-datepicker__month {
    background-color: var(--bg-card) !important;
  }

  .react-datepicker__triangle {
    display: none !important;
  }

  .react-datepicker__navigation-icon::before {
    border-color: var(--text-secondary) !important;
  }

  /* --- Screen-reader-only utility (visually hidden, accessible to AT) --- */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  @media (max-width: 768px) {
    .section {
      padding: var(--section-padding-mobile);
    }
  }
`;

export default GlobalStyles;
